// Which glass the cross is made of, and how the custom glass looks, as data.
// No DOM, no three.js: node tests check it, and the quality lab (?tune)
// builds its Glass controls and presets from it.
//
// Two glasses render the resin (crossScene.setGlass):
//   "mtm"     MeshTransmissionMaterial (@pmndrs/vanilla), the site's glass:
//             N jittered refraction samples per pixel per pass
//   "custom"  js/glass/custom-glass.js: the same two-interface refraction,
//             one lookup per pixel into a pre-blurred (mipmapped) buffer
//
// The page picks one from the URL (the options glass and look in
// js/options.js; glassFromOptions):
//   ?glass=mtm|custom    default mtm (the site as it ships)
//   &look=key:value,...  the custom glass's look knobs that differ from
//                        LOOK_DEFAULTS (formatLook); only with glass=custom
// Unknown values throw: a typo must not silently render the default glass.

export const GLASS_KINDS = Object.freeze(["mtm", "custom"]);

// The custom glass's look. Every one is a uniform (free to change), except
// where `apply` says "compile". LOOK_DEFAULTS is the "Match current" preset:
// the custom glass as close as it gets to the site's MTM glass.
export const LOOK_KNOBS = Object.freeze({
  ior: {
    kind: "number", min: 1, max: 2, step: 0.01, default: 1.5,
    label: "IOR",
    doc: "index of refraction: how sharply each surface bends the view (1 = no bend); also sets the reflectance at normal incidence ((n-1)/(n+1))^2",
  },
  exitRefraction: {
    kind: "number", min: 0, max: 1, step: 0.01, default: 0,
    label: "Exit refraction",
    doc: "how the back faces bend the ray leaving the resin: 0 = as MeshTransmissionMaterial does (as if entering it again: soft), 1 = physically (bent away, and past the critical angle reflected back inside: the crisp light and dark lines of clear acrylic, the studio seen in the ridges)",
  },
  thickness: {
    kind: "number", min: 0, max: 1.5, step: 0.01, default: 0.52,
    label: "Thickness (front)",
    doc: "how far, in the model's units, a ray refracted by the front faces travels before it reads what is behind: more = the interior displaced and magnified further",
  },
  interiorThickness: {
    kind: "number", min: 0, max: 1.5, step: 0.01, default: 0.32,
    label: "Thickness (back)",
    doc: "the same for the back faces' pass (the far side of the cross refracting the scene behind it): the ridges seen through the cross",
  },
  frost: {
    kind: "number", min: 0, max: 0.2, step: 0.002, default: 0.014,
    label: "Frost (blur)",
    doc: "the refraction's blur: the angular spread of the rays (radians, roughly) that a rough print scatters, face-on; seen obliquely the same rough surface spreads them more, and wider across the view. Each lookup reads the transmission buffer's mip level whose texels are that wide",
  },
  grain: {
    kind: "number", min: 0, max: 1, step: 0.01, default: 0.94,
    label: "Grain",
    doc: "per-pixel jitter of the lookup within the frost's width: the speckle of MeshTransmissionMaterial's few random samples (0 = perfectly smooth frost)",
  },
  dispersion: {
    kind: "number", min: 0, max: 0.1, step: 0.001, default: 0.01,
    label: "Dispersion",
    doc: "the IOR spread from red to blue (blue refracts with ior × (1 + dispersion)); costs two extra lookups, compiled out with the quality lab's Dispersion knob off",
  },
  roughness: {
    kind: "number", min: 0.03, max: 1, step: 0.01, default: 0.19,
    label: "Surface roughness",
    doc: "the surface's microfacet roughness for its reflections: lower = sharper highlights and environment strips",
  },
  reflection: {
    kind: "number", min: 0, max: 3, step: 0.01, default: 1,
    label: "Reflections",
    // 1 matches the MTM glass: its envMapIntensity (0.52) does nothing, as
    // three r169 lights a material without its own envMap by
    // scene.environmentIntensity (1) instead
    doc: "the environment's (the studio strips') reflection intensity; the Fresnel term decides how much shows at each angle",
  },
  specular: {
    kind: "number", min: 0, max: 4, step: 0.05, default: 1,
    label: "Highlights",
    doc: "the key and rim lights' specular highlights, as a multiple of their intensity",
  },
  tint: {
    kind: "color", default: "#f9fafa",
    label: "Tint",
    doc: "the resin's color after travelling the attenuation distance through it (Beer's law); white = clear",
  },
  attenuationDistance: {
    kind: "number", min: 0.5, max: 50, step: 0.5, default: 5,
    label: "Attenuation distance",
    doc: "how far light travels in the resin before taking on the tint fully (model units); shorter = a deeper tint in the thick parts",
  },
  normalStrength: {
    kind: "number", min: 0, max: 0.6, step: 0.01, default: 0.12,
    label: "Surface texture",
    doc: "the orange-peel normal map's strength (SLA layer lines and grain)",
  },
  distortion: {
    kind: "number", min: 0, max: 0.1, step: 0.002, default: 0.02,
    label: "Distortion",
    doc: "the printed-not-polished refraction wobble: the baked noise field's strength (js/distortion-field.js)",
  },
  cavity: {
    kind: "number", min: 0, max: 3, step: 0.05, default: 1,
    label: "Cavity",
    doc: "the baked groove darkening (cavity.png), as a multiple of the bake's",
  },
  ao: {
    kind: "number", min: 0, max: 3, step: 0.05, default: 1.3,
    label: "AO on reflections",
    doc: "the baked ambient occlusion's strength on the reflections (assets/ao.png): grooves reflect less of the studio",
  },
});

export const LOOK_DEFAULTS = Object.freeze(
  Object.fromEntries(Object.entries(LOOK_KNOBS).map(([key, spec]) => [key, spec.default])),
);

const COLOR = /^#[0-9a-f]{6}$/;

// `current` with `partial` applied, frozen. Throws on an unknown knob or a
// value outside its spec.
export function resolveLook(current, partial) {
  if (partial === null || typeof partial !== "object") throw new TypeError(`look: expected an object, got ${partial}`);
  const next = { ...current };
  for (const [key, value] of Object.entries(partial)) {
    const spec = LOOK_KNOBS[key];
    if (!spec) throw new TypeError(`look: unknown knob "${key}"; expected one of ${Object.keys(LOOK_KNOBS).join(", ")}`);
    if (spec.kind === "color") {
      if (typeof value !== "string" || !COLOR.test(value)) throw new TypeError(`look: ${key} must be a color like "#f9fafa" (lowercase), got ${value}`);
    } else if (typeof value !== "number" || !(value >= spec.min && value <= spec.max)) {
      throw new RangeError(`look: ${key} must be a number in ${spec.min}..${spec.max}, got ${value}`);
    }
    next[key] = value;
  }
  return Object.freeze(next);
}

// The knobs that differ from the defaults, "key:value" joined by commas
// (colors without their #): the URL's look= form. parseLook inverts it.
export function formatLook(look) {
  return Object.entries(look)
    .filter(([key, value]) => value !== LOOK_DEFAULTS[key])
    .map(([key, value]) => `${key}:${LOOK_KNOBS[key].kind === "color" ? value.slice(1) : value}`)
    .join(",");
}

export function parseLook(text) {
  const partial = {};
  for (const pair of text.split(",").filter(Boolean)) {
    const [key, raw, extra] = pair.split(":");
    const spec = LOOK_KNOBS[key];
    if (!spec || raw === undefined || raw === "" || extra !== undefined) throw new TypeError(`look: cannot read "${pair}"; expected knob:value`);
    partial[key] = spec.kind === "color" ? `#${raw}` : Number(raw);
  }
  return resolveLook(LOOK_DEFAULTS, partial);
}

// A glass setting: which glass, and (custom only) its look. Frozen; throws
// on anything else.
export function resolveGlass({ kind, look = undefined }) {
  if (!GLASS_KINDS.includes(kind)) throw new TypeError(`glass: kind must be one of ${GLASS_KINDS.join(", ")}, got ${kind}`);
  if (kind === "mtm") {
    if (look !== undefined && look !== null) throw new TypeError("glass: the mtm glass has no look knobs");
    return Object.freeze({ kind });
  }
  return Object.freeze({ kind, look: resolveLook(LOOK_DEFAULTS, look ?? {}) });
}

// The glass setting the page's options (js/options.js: glass, look) stand
// for, and back: the options that give `glass`. glass=auto (the default)
// stands for no one glass: the page decides it (js/glass/auto.js).
export function glassFromOptions({ glass, look }) {
  if (glass === "auto") throw new TypeError("glass=auto is no one glass: the page decides it (js/glass/auto.js)");
  return resolveGlass({ kind: glass, look: glass === "custom" ? parseLook(look ?? "") : undefined });
}
export function glassOptions(glass) {
  return { glass: glass.kind, look: glass.kind === "custom" ? formatLook(glass.look) || null : null };
}

// The built-in presets of the quality lab's Glass section. "Real piece" is
// the printed cross as the product photos show it: clear, glassy acrylic
// rather than frosted — crisp refraction lines through the ridges, sharp
// reflections of the studio, the interior (the drive) legible.
export const BUILTIN_PRESETS = Object.freeze([
  Object.freeze({ name: "Current site (MTM)", glass: resolveGlass({ kind: "mtm" }) }),
  Object.freeze({ name: "Match current", glass: resolveGlass({ kind: "custom" }) }),
  Object.freeze({
    name: "Real piece",
    glass: resolveGlass({
      kind: "custom",
      look: {
        exitRefraction: 0.45,
        thickness: 0.6,
        interiorThickness: 0.4,
        frost: 0.004,
        grain: 0,
        dispersion: 0.008,
        roughness: 0.06,
        specular: 1.4,
        tint: "#f4f6f7",
        normalStrength: 0.05,
        distortion: 0.01,
        cavity: 0.6,
      },
    }),
  }),
]);
